/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    ISignUpGivenNameVerification,
    ISignUpItem,
    ISignUpDropDown,
    ISignUpLoading,
    ISignUpSurnameVerification,
    // ISignUpMobileNumberVerification,
    ISignUpViewProps
} from './cz-sign-up';
import { useEffect, useRef } from 'react';

const SignUpItem: React.FC<ISignUpItem> = ({ wrapper, label, errorMessage, input }) => {
    return (
        <Node {...wrapper}>
            {label}
            {errorMessage}
            {input}
        </Node>
    );
};
const SignUpDropDown: React.FC<ISignUpDropDown> = ({ wrapper, label, errorMessage, input }) => {
    return (
        <Node {...wrapper}>
            {label}
            {errorMessage}
            {input}
        </Node>
    );
};

const SignUpEmailVerification: React.FC<any> = ({
    email,
    buttonWrapper,
    buttons,
    successMessage,
    errorMessage,
    showVerificationTick,
    headerRef,
    resources
}) => {
    return (
        <>
            <SignUpItem {...email} />
            {errorMessage &&
                errorMessage.map((message: React.ReactNode) => {
                    return <>{message}</>;
                })}
            <h3 className={'ms-sign-up__page-success-header'} style={{ display: 'none' }} ref={headerRef}>
                {resources.emailInfoHeader}
            </h3>
            <span ref={showVerificationTick}>
                {successMessage &&
                    successMessage.map((message: React.ReactNode) => {
                        return <>{message}</>;
                    })}
            </span>
            <Node {...buttonWrapper}>
                {buttons &&
                    buttons.map((button: React.ReactNode) => {
                        return <>{button}</>;
                    })}
            </Node>
        </>
    );
};

/**
 * Handles validation of sign-up givenName.
 * @param event - Content editable event.
 * @param event.givenName - Event Given Name.
 * @param event.errorMessage - Event Error Message.
 * @returns - Error Message for Given Name.
 */
const SignUpGivenNameVerificationComponent: React.FC<ISignUpGivenNameVerification> = ({ givenName, errorMessage }) => {
    return (
        <>
            <SignUpItem {...givenName} />
            {errorMessage.map((message: React.ReactNode) => {
                return <>{message}</>;
            })}
        </>
    );
};

/**
 * Handles validation of signUp surname.
 * @param event - Content editable event.
 * @param event.surname - Event surname.
 * @param event.errorMessage - Event Error Message.
 * @returns - Error Message for surname.
 */
const SignUpSurnameVerificationComponent: React.FC<ISignUpSurnameVerification> = ({ surname, errorMessage }) => {
    return (
        <>
            <SignUpItem {...surname} />
            {errorMessage.map((message: React.ReactNode) => {
                return <>{message}</>;
            })}
        </>
    );
};
// const SignUpMobileNumberVerificationComponent: React.FC<ISignUpMobileNumberVerification> = ({ extension_MobileNumber, errorMessage }) => {
//     return (
//         <>
//             <SignUpItem {...extension_MobileNumber} />
//             {errorMessage.map((message: React.ReactNode) => {
//                 return <>{message}</>;
//             })}
//         </>
//     );
// };

const LocalAccount: React.FC<any> = ({
    localAccount,
    items,
    emailVerification,
    givenNameVerification,
    mobileNumberVerification,
    surnameVerification,
    buttons,
    errorMessage,
    disclaimer,
    headerRef,
    showVerificationTick,
    resources,
    config
}) => {
    return (
        <Node {...localAccount}>
            {items &&
                items.map((item: ISignUpItem, index: number) => {
                    if (item.key === 'email' && emailVerification.isRequired) {
                        return (
                            <Node key={item.key} {...emailVerification.verificationControlWrapper}>
                                <SignUpItem {...item} />
                                <SignUpEmailVerification
                                    {...emailVerification}
                                    headerRef={headerRef}
                                    showVerificationTick={showVerificationTick}
                                    resources={resources}
                                />
                            </Node>
                        );
                    }
                    if (item.key === 'givenName' && givenNameVerification.isRequired) {
                        return (
                            <Node key={item.key} {...givenNameVerification.verificationControlWrapper}>
                                <SignUpItem {...item} />
                                <SignUpGivenNameVerificationComponent {...givenNameVerification} />
                            </Node>
                        );
                    }
                    if (item.key === 'surname' && surnameVerification.isRequired) {
                        return (
                            <Node key={item.key} {...surnameVerification.verificationControlWrapper}>
                                <SignUpItem {...item} />
                                <SignUpSurnameVerificationComponent {...surnameVerification} />
                            </Node>
                        );
                    }
                    if (item.key === 'extension_MobileNumber') {
                        return (
                            <>
                                <SignUpItem {...item} />
                                {config.country === 'CAN' && (
                                    <div className={'ms-sign-up__account-item-birthday-greet-msg'}>
                                        {config.mobileFindYourAccountMessage}
                                    </div>
                                )}
                            </>
                        );
                    }
                    if (item.key === 'extension_BirthDate') {
                        // eslint-disable-next-line react/jsx-key
                        return (
                            <>
                                <SignUpDropDown {...item} />
                                <div className={'ms-sign-up__account-item-birthday-greet-msg'}>{config.sendBirthdayGiftMessage}</div>
                            </>
                        );
                    }
                    if (item.key === 'extension_BirthMonth') {
                        // eslint-disable-next-line react/jsx-key
                        return (
                            <>
                                <SignUpDropDown {...item} />
                            </>
                        );
                    }
                    if (item.key === 'newPassword') {
                        // eslint-disable-next-line react/jsx-key
                        return (
                            <>
                                <h3 className={'ms-sign-up__page-password-header'}>{resources.passwordHeader}</h3>
                                <SignUpItem {...item} />
                            </>
                        );
                    }
                    // eslint-disable-next-line react/jsx-key
                    return <SignUpItem {...item} />;
                })}
            {errorMessage &&
                errorMessage.map((error: React.ReactNode, index: number) => {
                    return <React.Fragment key={index}>{error}</React.Fragment>;
                })}
            {buttons &&
                buttons.map((button: React.ReactNode, index: number) => {
                    return <React.Fragment key={index}>{button}</React.Fragment>;
                })}
            {disclaimer}
        </Node>
    );
};

const SignUpLoading: React.FC<ISignUpLoading> = ({ modal, modalBody, icon, message }) => {
    return (
        <Node {...modal}>
            <Node {...modalBody}>
                {icon}
                {message}
            </Node>
        </Node>
    );
};

const SignUpView: React.FC<ISignUpViewProps> = props => {
    const { signUp, viewState, loading, defaultAADConainer, aadConainer, heading, signUpLocalAccount, resources, config } = props;
    const targetRef = useRef<any>();
    const headerRef = useRef<any>();
    const showVerificationTick = useRef<any>();
    const observer = useRef<any>(null);
    useEffect(() => {
        targetRef.current = document.getElementById('emailVerificationControl_success_message');
        if (!observer.current && targetRef.current) {
            const MutationObserver = window.MutationObserver;

            function callback(mutations: any) {
                for (const mutation of mutations) {
                    if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
                        if (targetRef.current.style.display === 'none') {
                            headerRef.current.style.display = 'none';
                            showVerificationTick.current.classList.remove('hide-verification-tick');
                        } else {
                            if (targetRef.current.innerHTML.indexOf('verification') !== -1) {
                                headerRef.current.style.display = 'inline';
                                showVerificationTick.current.classList.add('hide-verification-tick');
                            }
                        }
                    }
                }
            }

            const options = {
                childList: true,
                attributes: true
            };
            // create the observer
            observer.current = new MutationObserver(callback);
            observer.current.observe(targetRef.current, options);
        }
    });
    return (
        <Module {...signUp}>
            {viewState.isShowLoading && <Node {...defaultAADConainer} />}
            {viewState.isShowLoading && <SignUpLoading {...loading} />}
            <Node {...aadConainer}>
                {heading}
                {LocalAccount({ ...signUpLocalAccount, headerRef, showVerificationTick, resources, config })}
            </Node>
        </Module>
    );
};

export default SignUpView;
